/**
 * Collection Grid ATC Module
 * Inline add-to-cart for every product grid (snippets/grid-product-atc.liquid:
 * collection and search grids, recommendations, featured collections, recently
 * viewed, the menu, cart recommendations) and the product page's order table.
 * layout/theme.liquid loads it once, on every page; never load a second copy.
 *
 * Features:
 * - Single-variant products: +/- quantity selector directly on card
 * - Multi-variant products: "Choose Options" button → modal with all variants
 * - Toast notifications on add
 * - Optimistic UI with queue-based cart sync
 * - The cart is read once, when a control first needs it
 */

const CollectionATC = (function() {
  'use strict';

  const COLLECTION_ATC_BINDINGS_READY = '__collectionATCBindingsReady';
  let collectionATCInitPromise = null;

  /* ========================================================================
     MODULE: Store (Centralized State Management)
     ======================================================================== */
  const Store = {
    state: {
      cartByVariant: new Map(),
      linesByVariant: new Map(),
      itemCount: null,
      // True once a verified cart has been read (syncCart).
      cartLoaded: false,
      cartBusy: false
    },
    listeners: new Set(),

    getState() {
      return this.state;
    },

    setState(updates) {
      this.state = { ...this.state, ...updates };
      this.notify();
    },

    subscribe(fn) {
      this.listeners.add(fn);
      return () => this.listeners.delete(fn);
    },

    notify() {
      this.listeners.forEach(fn => fn(this.state));
    }
  };

  /* ========================================================================
     MODULE: CartSync (AJAX Cart with Retry Logic)
     ======================================================================== */
  const CartSync = {
    pendingUpdates: new Map(),
    activeVariants: new Set(),
    updateInFlight: false,
    // HC - Track retries for batch cart updates - 2026-01-27
    retryCounts: new Map(),
    updateBatchBaseline: null,
    maxRetries: 3,
    toastTimer: null,
    pendingToastDelta: new Map(),
    pendingToastMeta: new Map(),
    pendingToastEl: null,
    pendingToastContainer: null,
    challengeParkedToastVisible: false,
    liveRegion: null,

    // HC - Exponential backoff with jitter for retries - 2026-01-30
    getRetryDelay(variantIds) {
      const baseDelay = 500;
      const maxDelay = 4000;
      let attempts = 1;

      if (Array.isArray(variantIds) && variantIds.length) {
        attempts = Math.max(...variantIds.map((id) => this.retryCounts.get(Number(id)) || 1));
      } else if (this.retryCounts.size > 0) {
        attempts = Math.max(...this.retryCounts.values());
      }

      const expDelay = Math.min(baseDelay * Math.pow(2, Math.max(0, attempts - 1)), maxDelay);
      const jitter = Math.floor(Math.random() * 200);
      return expDelay + jitter;
    },

    getUpdateBatchBaseline(store) {
      if (!this.updateBatchBaseline) {
        this.updateBatchBaseline = {
          cartByVariant: new Map(store.state.cartByVariant),
          itemCount: typeof store.state.itemCount === 'number' ? store.state.itemCount : null
        };
      }

      return this.updateBatchBaseline;
    },

    resetUpdateBatchBaseline() {
      this.updateBatchBaseline = null;
    },

    getOptimisticItemCount(baseline, updateTargets) {
      if (!baseline || typeof baseline.itemCount !== 'number') return null;

      let itemCount = baseline.itemCount;
      updateTargets.forEach((qty, id) => {
        if (typeof qty !== 'number') return;
        const variantId = Number(id);
        itemCount += qty - (baseline.cartByVariant.get(variantId) || 0);
      });

      return Math.max(0, itemCount);
    },

    accumulateToast(variantId, delta, title, image) {
      const currentDelta = this.pendingToastDelta.get(variantId) || 0;
      this.pendingToastDelta.set(variantId, currentDelta + delta);
      this.pendingToastMeta.set(variantId, { title, image });
    },

    flushToast(variantId) {
      const qty = this.pendingToastDelta.get(variantId);
      if (!qty) return;

      this.challengeParkedToastVisible = false;
      const meta = this.pendingToastMeta.get(variantId) || {};
      const { title, image } = meta;

      this.toast({
        title,
        variantTitle: title,
        image,
        quantity: qty,
        type: qty > 0 ? 'added' : 'removed'
      });

      this.pendingToastDelta.delete(variantId);
      this.pendingToastMeta.delete(variantId);
    },

    ensureToastContainer() {
      let container = document.body.querySelector(':scope > .cg-toast-container');
      if (!container) {
        container = document.createElement('div');
        container.className = 'cg-toast-container';
        container.setAttribute('popover', 'manual');
        document.body.appendChild(container);
      }
      if (!container.getAttribute || !container.getAttribute('role')) {
        container.setAttribute('role', 'status');
      }
      if (!container.getAttribute || !container.getAttribute('aria-live')) {
        container.setAttribute('aria-live', 'polite');
      }
      if (!container.getAttribute || !container.getAttribute('aria-atomic')) {
        container.setAttribute('aria-atomic', 'true');
      }
      try {
        if (!container.matches(':popover-open')) {
          container.showPopover();
        }
      } catch (e) {}
      return container;
    },

    offsetChallengeToastContainer(container) {
      if (!container || !container.style) return;
      // Keep parked challenge toasts below the notice pill and under the cart
      // drawer layer: notice z-index 9000, drawer z-index 99999, cg toast 1500.
      container.style.top = '72px';
      container.style.zIndex = '1500';
    },

    setToastContent(toast, { image, line1, line2 }) {
      toast.innerHTML = '';

      if (image) {
        const img = document.createElement('img');
        img.src = image;
        img.alt = '';
        img.className = 'cg-toast__image';
        toast.appendChild(img);
      }

      const textWrap = document.createElement('div');
      textWrap.className = 'cg-toast__text';

      const line1El = document.createElement('div');
      line1El.className = 'cg-toast__line';
      line1El.textContent = line1 || '';

      const line2El = document.createElement('div');
      line2El.className = 'cg-toast__line cg-toast__line--meta';
      line2El.textContent = line2 || '';

      textWrap.appendChild(line1El);
      textWrap.appendChild(line2El);
      toast.appendChild(textWrap);
    },

    showToastElement(toast, container, { autoDismiss = true } = {}) {
      if (!container.contains(toast)) {
        container.appendChild(toast);
      }

      if (!toast.classList.contains('is-visible')) {
        requestAnimationFrame(() => {
          toast.classList.add('is-visible');
        });
      }

      if (!autoDismiss) return;

      setTimeout(() => {
        toast.classList.remove('is-visible');
        setTimeout(() => {
          toast.remove();
          if (!container.hasChildNodes()) {
            try { container.hidePopover(); } catch (e) {}
            container.remove();
          }
        }, 300);
      }, 2500);
    },

    showPendingToast() {
      if (this.pendingToastEl) return;

      const container = this.ensureToastContainer();
      const toast = document.createElement('div');
      toast.className = 'cg-toast';
      toast.dataset.cgPending = 'true';

      this.setToastContent(toast, {
        line1: 'Adding your products',
        line2: ''
      });

      this.pendingToastEl = toast;
      this.pendingToastContainer = container;
      this.showToastElement(toast, container, { autoDismiss: false });
    },

    showChallengeParkedToast(message) {
      const line1 = 'Cart paused';
      const line2 = message || 'Quick security check in progress — your items will be added automatically in a moment.';
      this.challengeParkedToastVisible = true;

      if (this.pendingToastEl) {
        const container = this.pendingToastContainer || this.pendingToastEl.parentElement || this.ensureToastContainer();
        this.offsetChallengeToastContainer(container);
        this.pendingToastEl.dataset.cgPending = 'true';
        this.setToastContent(this.pendingToastEl, { line1, line2 });
        this.pendingToastContainer = container;
        this.showToastElement(this.pendingToastEl, container, { autoDismiss: false });
        return;
      }

      const container = this.ensureToastContainer();
      this.offsetChallengeToastContainer(container);
      const toast = document.createElement('div');
      toast.className = 'cg-toast';
      toast.dataset.cgPending = 'true';

      this.setToastContent(toast, { line1, line2 });

      this.pendingToastEl = toast;
      this.pendingToastContainer = container;
      this.showToastElement(toast, container, { autoDismiss: false });
    },

    replacePendingToast({ image, line1, line2 }) {
      if (!this.pendingToastEl) return false;

      const toast = this.pendingToastEl;
      const container = this.pendingToastContainer || toast.parentElement || this.ensureToastContainer();

      this.pendingToastEl = null;
      this.pendingToastContainer = null;
      delete toast.dataset.cgPending;

      this.setToastContent(toast, { image, line1, line2 });
      this.showToastElement(toast, container, { autoDismiss: true });
      return true;
    },

    clearPendingToast() {
      this.challengeParkedToastVisible = false;
      if (!this.pendingToastEl) return;

      const toast = this.pendingToastEl;
      const container = this.pendingToastContainer || toast.parentElement;

      this.pendingToastEl = null;
      this.pendingToastContainer = null;
      delete toast.dataset.cgPending;

      toast.remove();
      if (container && !container.hasChildNodes()) {
        try { container.hidePopover(); } catch (e) {}
        container.remove();
      }
    },

    toast({ title, variantTitle, image, quantity, type }) {
      const message = type === 'removed'
        ? `${title || 'Item'} removed from cart`
        : `Added ${quantity || ''} ${variantTitle || title || 'item'} to cart`.trim();

      // Try theme's toast first
      try {
        if (window.Theme && typeof window.Theme.showToast === 'function') {
          window.Theme.showToast({
            title: message,
            image,
            productTitle: title,
            variantTitle,
            quantity
          });
          this.clearPendingToast();
          return;
        }
      } catch (e) {}

      const line1 = type === 'removed' ? 'Removed from cart' : 'Added to cart';
      const line2 = `${quantity ? quantity + ' × ' : ''}${variantTitle || title || ''}`.trim();

      if (this.replacePendingToast({ image, line1, line2 })) {
        return;
      }

      const container = this.ensureToastContainer();
      const toast = document.createElement('div');
      toast.className = 'cg-toast';
      this.setToastContent(toast, { image, line1, line2 });
      this.showToastElement(toast, container);
    },

    // HC - Visible error toast for failed cart updates - 2026-02-02
    showError(message) {
      if (!message) return;

      this.challengeParkedToastVisible = false;
      try {
        if (window.Theme && typeof window.Theme.showToast === 'function') {
          window.Theme.showToast({ title: message });
          this.clearPendingToast();
          return;
        }
      } catch (e) {}

      const line1 = 'Cart update failed';
      const line2 = message;

      if (this.replacePendingToast({ line1, line2 })) {
        return;
      }

      const container = this.ensureToastContainer();
      const toast = document.createElement('div');
      toast.className = 'cg-toast';
      this.setToastContent(toast, { line1, line2 });
      this.showToastElement(toast, container);
    },

    // HC - Revert optimistic qty on final failure - 2026-02-02
    revertQuantity(variantId, qty) {
      document.querySelectorAll(`.cg-qty[data-variant-id="${variantId}"] .cg-qty-input`).forEach((input) => {
        input.value = String(qty);
      });
    },

    // HC - One quantity per variant: the sum of its cart lines (a promo or
    // gift line can share the variant with the paid line), plus the lines
    // themselves so writes can target the paid one (cartTarget) - 2026-09-26
    syncCart(store, cart) {
      if (!cart || !Array.isArray(cart.items)) return false;

      const map = new Map();
      const lines = new Map();
      (cart.items || []).forEach(item => {
        const quantity = Number(item.quantity) || 0;
        map.set(item.variant_id, (map.get(item.variant_id) || 0) + quantity);
        if (!lines.has(item.variant_id)) lines.set(item.variant_id, []);
        lines.get(item.variant_id).push({ key: item.key, quantity, plain: isPlainLine(item) });
      });

      store.setState({
        cartByVariant: map,
        linesByVariant: lines,
        itemCount: typeof cart.item_count === 'number' ? cart.item_count : null,
        cartLoaded: true
      });
      return true;
    },

    // A variant's cart lines: the plain (paid) line a stepper writes, and the
    // units on its other lines (gift, promo, bundle), which a stepper leaves
    // alone. With no paid line, every unit is locked.
    splitLines(variantId, store) {
      const lines = (store.state.linesByVariant && store.state.linesByVariant.get(variantId)) || [];
      const paid = (lines.length === 1 && lines[0].plain)
        ? lines[0]
        : (lines.find((line) => line.plain && line.key) || null);
      const locked = lines.reduce((sum, line) => sum + (line === paid ? 0 : line.quantity), 0);
      return { lines, paid, locked };
    },

    // Where a stepper's absolute quantity lands. updates[variantId] only
    // reaches the variant's first cart line, so when another line (gift,
    // promo, bundle) shares the variant, write the paid line by its key and
    // keep the other lines' units. null: no paid line, nothing a stepper can
    // safely change.
    cartTarget(variantId, targetQty, store) {
      const { lines, paid, locked } = this.splitLines(variantId, store);
      if (!lines.length || (lines.length === 1 && lines[0].plain)) {
        return { key: variantId, quantity: targetQty };
      }
      if (!paid) return null;
      return { key: paid.key, quantity: Math.max(0, targetQty - locked) };
    },

    getAddedProductMeta(variantId) {
      const sourceQty = document.querySelector(`.cg-qty[data-variant-id="${variantId}"]`);
      // HC - Generic toast contract for steppers outside grid cards (the product
      // page's order table): [data-cg-item] row, [data-cg-title], [data-cg-image],
      // and data-cg-sku where data-sku would collide with theme.js - 2026-09-25
      const context = sourceQty?.closest('[data-cg-item]') || sourceQty?.closest('.grid-product') || sourceQty?.closest('.grid-item') || sourceQty?.closest('.cg-modal__variant') || sourceQty?.closest('.color-grid__card');
      const titleEl = context?.querySelector('[data-cg-title]') || context?.querySelector('.grid-product__title') || context?.querySelector('.cg-modal__variant-title') || context?.querySelector('.color-grid__card-title');
      const imgEl = context?.querySelector('[data-cg-image]')
                 || context?.querySelector('.grid-product__image-wrap img')
                 || context?.querySelector('.grid-product__image')
                 || context?.querySelector('.cg-modal__variant-image img')
                 || context?.querySelector('.color-grid__card-image');
      const title = titleEl?.textContent?.trim() || '';
      const sku = sourceQty?.dataset?.sku || sourceQty?.dataset?.cgSku || '';
      let image = '';
      if (imgEl) {
        if (imgEl.currentSrc) {
          image = imgEl.currentSrc;
        } else if (imgEl.src && !imgEl.src.includes('{width}')) {
          image = imgEl.src;
        } else if (imgEl.dataset?.src) {
          image = imgEl.dataset.src.replace('{width}', '200');
        }
      }

      return { title, sku, image };
    },

    async fetchCart(store, options = {}) {
      // Don't fan out a cart read at a challenged session; getCart would just
      // return null anyway. The next successful read elsewhere clears the flag.
      if (window.theme && window.theme.cart && window.theme.cart.isChallengeActive &&
          window.theme.cart.isChallengeActive()) {
        return;
      }
      const { broadcast = false } = options;
      try {
        const cart = await window.theme.cart.getCart();
        if (!cart) return;

        if (!this.syncCart(store, cart)) return;

        if (broadcast) {
          this.broadcastThemeCart(cart);
        }
      } catch (e) {
        console.error('Failed to fetch cart:', e);
      }
    },

    // HC - The cart is read when something first needs it, and once: a
    // stepper showing when the page loads, a tap or typed quantity, Choose
    // Options. Callers at the same time share the read. Resolves true once
    // the cart is known (false: the read failed or a security check is
    // running) - 2026-09-26
    cartRead: null,
    ensureCart(store) {
      if (store.state.cartLoaded) return Promise.resolve(true);
      if (!this.cartRead) {
        this.cartRead = this.fetchCart(store).then(() => {
          this.cartRead = null;
          return !!store.state.cartLoaded;
        });
      }
      return this.cartRead;
    },

    async update(variantId, qty, store) {
      this.queueUpdate(variantId, qty, store);
    },

    ensureLiveRegion() {
      if (this.liveRegion && this.liveRegion.parentNode) {
        return this.liveRegion;
      }

      let liveRegion = document.querySelector('[data-cg-live-region]');
      if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.className = 'visually-hidden';
        liveRegion.setAttribute('data-cg-live-region', 'true');
        liveRegion.setAttribute('role', 'status');
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.setAttribute('aria-atomic', 'true');
        document.body.appendChild(liveRegion);
      }
      this.liveRegion = liveRegion;
      return liveRegion;
    },

    announce(message) {
      const liveRegion = this.ensureLiveRegion();
      if (liveRegion) {
        liveRegion.textContent = message;
        setTimeout(() => { liveRegion.textContent = ''; }, 1000);
      }
    },

    broadcastThemeCart(payload, options = {}) {
      if (!payload) return;
      const { build = false } = options;
      const source = options.source || 'collection-atc';

      window.theme.cart.broadcastLineUpdate(payload, {
        source,
        addedProducts: options.addedProducts || []
      });

      if (build) {
        document.dispatchEvent(new CustomEvent('cart:build', {
          detail: {
            source: 'collection-atc',
            intent: 'LINE_UPDATE'
          }
        }));
      }
    },

    broadcastLightweightCartCount(itemCount, options = {}) {
      if (typeof itemCount !== 'number') return;

      window.theme.cart.dispatchCartCount(itemCount, {
        intent: options.intent || window.theme.cart.intents.COUNT_ONLY,
        source: 'collection-atc'
      });
    },

    queueUpdate(variantId, qty, store) {
      this.pendingUpdates.set(variantId, qty);
      if (this.updateInFlight) return;
      this.processQueue(store);
    },

    async processQueue(store) {
      // HC - cart telemetry - 2026-07-04
      var T = window.IBCartTelemetry; T && T.track('cg:queue', { pending: this.pendingUpdates.size, inFlight: !!this.updateInFlight });
      // Don't drain the queue at a challenged session; the write would just be
      // rejected and rapid clicks would spin setTimeout(0) and repeat the toast.
      // Pending updates stay queued and drain once the challenge clears.
      if (window.theme && window.theme.cart && window.theme.cart.isChallengeActive &&
          window.theme.cart.isChallengeActive()) {
        // HC - cart telemetry - 2026-07-04
        T && T.track('cg:parked');
        this.showChallengeParkedToast('Quick security check in progress — your items will be added automatically in a moment.');
        return;
      }
      if (this.updateInFlight) return;
      if (this.pendingUpdates.size === 0) return;

      this.updateInFlight = true;

      const updates = {};
      const updateTargets = new Map();
      const prevQtyByVariant = new Map();
      const updateBaseline = this.getUpdateBatchBaseline(store);
      const prevItemCount = updateBaseline.itemCount;

      this.pendingUpdates.forEach((qty, id) => {
        if (typeof qty !== 'number') return;
        const variantId = Number(id);
        updates[variantId] = qty;
        updateTargets.set(variantId, qty);
        prevQtyByVariant.set(variantId, updateBaseline.cartByVariant.get(variantId) || 0);
        this.activeVariants.add(variantId);
        this.togglePendingState(variantId, true);
      });

      const updateKeys = Object.keys(updates);
      if (updateKeys.length === 0) {
        this.updateInFlight = false;
        this.resetUpdateBatchBaseline();
        return;
      }

      // HC - Track error state for retry logic (handles 409 conflicts) - 2026-01-27
      let shouldRetry = false;

      try {
        const optimisticItemCount = this.getOptimisticItemCount(updateBaseline, updateTargets);
        if (optimisticItemCount !== null) {
          if (optimisticItemCount !== prevItemCount) {
            store.setState({ itemCount: optimisticItemCount });
            this.broadcastLightweightCartCount(optimisticItemCount, {
              intent: window.theme.cart.intents.COUNT_ONLY_OPTIMISTIC
            });
          }
        }

        const addedProductMetaByVariant = new Map();
        const addedProducts = [];
        updateKeys.forEach((id) => {
          const variantId = Number(id);
          const targetQty = updates[id];
          const prevQty = prevQtyByVariant.get(variantId) || 0;
          const delta = targetQty - prevQty;
          if (delta <= 0) return;

          const meta = this.getAddedProductMeta(variantId);
          addedProductMetaByVariant.set(variantId, meta);
          addedProducts.push({
            variant_id: variantId,
            sku: meta.sku,
            quantity: delta,
            title: meta.title,
            image: meta.image
          });
        });

        const payload = {};
        updateKeys.forEach((id) => {
          const target = this.cartTarget(Number(id), updates[id], store);
          if (target) payload[target.key] = target.quantity;
        });

        const result = await window.theme.cart.updateCartAndBroadcast(payload, {
          source: 'collection-atc',
          addedProducts
        });

        const cart = window.theme.cart.getCartState(result);
        if (!this.syncCart(store, cart)) {
          throw new Error('Cart helper returned no verified cart state');
        }

        updateKeys.forEach((id) => {
          const variantId = Number(id);
          const targetQty = updates[id];
          const prevQty = prevQtyByVariant.get(variantId) || 0;

          document.dispatchEvent(new CustomEvent('cg:cart:updated', {
            detail: { variantId, quantity: targetQty, action: 'update' }
          }));

          // What the cart now holds, which can differ from the request.
          this.announce(`Updated quantity to ${store.state.cartByVariant.get(variantId) || 0}`);
          this.retryCounts.delete(variantId);

          const delta = targetQty - prevQty;
          if (delta > 0) {
            const meta = addedProductMetaByVariant.get(variantId) || this.getAddedProductMeta(variantId);
            this.accumulateToast(variantId, delta, meta.title, meta.image);

            const nextTarget = this.pendingUpdates.get(variantId);
            const hasMoreUpdates = typeof nextTarget === 'number' && nextTarget !== targetQty;

            if (!hasMoreUpdates) {
              this.flushToast(variantId);
            }
          }
        });
        this.resetUpdateBatchBaseline();
      } catch (e) {
        console.error('Failed to update cart:', e);
        const status = e && typeof e.status === 'number' ? e.status : null;
        // Only a real security challenge must skip retry — gate on the explicit
        // challenge signal, NOT e.retryable (which is false for every non-429 error,
        // so it would wrongly suppress legitimate 409 / 500 / 502 / 504 retries).
        const isSecurityChallenge = !!(e && e.isSecurityChallenge);
        const retryable = !isSecurityChallenge &&
          (status === 409 || status === 429 || (status >= 500 && status < 600) || status === null);
        shouldRetry = retryable;

        let hasFinalFailure = false;
        const finalReverts = [];
        updateKeys.forEach((id) => {
          const variantId = Number(id);
          if (retryable) {
            const attempts = (this.retryCounts.get(variantId) || 0) + 1;
            this.retryCounts.set(variantId, attempts);
            if (attempts < this.maxRetries) return;
          }

          hasFinalFailure = true;
          this.retryCounts.delete(variantId);
          // Only discard if the user hasn't queued a newer value during retries
          if (this.pendingUpdates.get(variantId) === updates[id]) {
            this.pendingUpdates.delete(variantId);
            finalReverts.push({ variantId, fallbackQty: prevQtyByVariant.get(variantId) || 0 });
          }
          this.togglePendingState(variantId, false);
          this.activeVariants.delete(variantId);
          this.pendingToastDelta.delete(variantId);
          this.pendingToastMeta.delete(variantId);
        });

        if (hasFinalFailure) {
          // HC - Sync to server truth before reverting - 2026-06-10
          // The request may have been applied even though the response never
          // arrived; prefer Shopify's actual quantities over a blind revert.
          // Skip the truth-fetch during a challenge — it would just hit the
          // challenged endpoint again. Fall back to the local optimistic revert.
          const truthCart = (finalReverts.length && !isSecurityChallenge)
            ? await window.theme.cart.getCart()
            : null;

          if (truthCart && this.syncCart(store, truthCart)) {
            const truthByVariant = new Map();
            truthCart.items.forEach((item) => {
              truthByVariant.set(item.variant_id, (truthByVariant.get(item.variant_id) || 0) + (Number(item.quantity) || 0));
            });
            finalReverts.forEach(({ variantId }) => {
              this.revertQuantity(variantId, truthByVariant.get(variantId) || 0);
            });
            this.broadcastLightweightCartCount(truthCart.item_count, {
              intent: window.theme.cart.intents.COUNT_ONLY
            });
          } else {
            finalReverts.forEach(({ variantId, fallbackQty }) => {
              this.revertQuantity(variantId, fallbackQty);
            });
            const rollbackItemCount = this.getOptimisticItemCount(updateBaseline, this.pendingUpdates);
            if (rollbackItemCount !== null && rollbackItemCount !== store.state.itemCount) {
              store.setState({ itemCount: rollbackItemCount });
              this.broadcastLightweightCartCount(rollbackItemCount, {
                intent: window.theme.cart.intents.COUNT_ONLY_OPTIMISTIC
              });
            }
          }

          // Prefer the error's userMessage so an edge-challenge surfaces the
          // security-check message instead of a misleading generic cart error.
          const message = (e && e.userMessage) || 'Unable to update cart. Please try again.';
          this.announce(message);
          this.showError(message);
        }
      } finally {
        updateKeys.forEach((id) => {
          const variantId = Number(id);
          const targetQty = updates[id];
          this.activeVariants.delete(variantId);
          const latest = this.pendingUpdates.get(variantId);
          if (!shouldRetry && typeof latest === 'number' && latest === targetQty) {
            this.pendingUpdates.delete(variantId);
            this.togglePendingState(variantId, false);
          }
        });

        this.updateInFlight = false;

        // The cart syncs above skipped these fields while they were pending;
        // show what the cart actually holds now that they have settled.
        syncAllQuantities();

        if (this.pendingUpdates.size === 0) {
          this.resetUpdateBatchBaseline();
          if (!this.updateInFlight && this.challengeParkedToastVisible) {
            this.clearPendingToast();
          }
        }

        if (this.pendingUpdates.size > 0) {
          // Retry after delay to let concurrent cart operations settle (409 conflicts)
          const delay = shouldRetry ? this.getRetryDelay(updateKeys) : 0;
          setTimeout(() => this.processQueue(store), delay);
        }
      }
    },

    togglePendingState(variantId, isPending) {
      const selector = `.cg-qty[data-variant-id="${variantId}"]`;
      document.querySelectorAll(selector).forEach((qtyWrap) => {
        const actions = qtyWrap.closest('.cg-actions');
        if (!actions) return;
        actions.classList.toggle('is-pending', isPending);
        actions.setAttribute('aria-busy', isPending ? 'true' : 'false');
      });
    }
  };

  /* ========================================================================
     MODULE: QuantityManager (Optimistic Updates)
     ======================================================================== */
  const QuantityManager = {
    setup(container, store) {
      const qtyWrap = container.querySelector('.cg-qty');
      if (!qtyWrap) return;

      // Contract: steppers marked data-atc-external have their cart writes owned
      // by another module. CollectionATC must not bind quantity controls or sync
      // quantities into them.
      if (qtyWrap.dataset.atcExternal) return;

      // Idempotency: reinitQuickAdd can run setup twice over page-1 cards on
      // multi-page collections (and init() re-scans all .cg-actions). Without a
      // guard, click handlers bind twice and one "+" click adds quantity 2. Bind
      // listeners at most once per stepper; first-time setup is unaffected.
      if (qtyWrap.dataset.spQtyBound) return;
      qtyWrap.dataset.spQtyBound = '1';

      const minus = qtyWrap.querySelector('.cg-qty-btn--minus');
      const plus = qtyWrap.querySelector('.cg-qty-btn--plus');
      const input = qtyWrap.querySelector('.cg-qty-input');
      const variantId = parseInt(qtyWrap.dataset.variantId);
      const isAvailable = qtyWrap.dataset.available !== 'false';

      if (!variantId || !input) return;

      // HC - Order-safe steppers (product page order table + grid) - 2026-09-26
      // Ceiling: data-max when the markup sets one (the order table uses the
      // main quantity's stock rule), else 99; nothing more can be added to an
      // unavailable variant, but one already in the cart can still come down.
      const declaredMax = parseInt(qtyWrap.dataset.max, 10);
      const maxQty = !isAvailable ? 0 : (isNaN(declaredMax) ? 99 : Math.max(0, declaredMax));

      const effectiveCurrentQty = () => {
        const pendingTarget = CartSync.pendingUpdates.get(variantId);
        return typeof pendingTarget === 'number'
          ? pendingTarget
          : (store.state.cartByVariant.get(variantId) || 0);
      };

      const updateQty = async (requested) => {
        const current = effectiveCurrentQty();
        // Never past the ceiling, and never below what is already in the cart
        // on the way up: + on a line above the ceiling does nothing rather
        // than cutting it back. Never below the units on gift or promo lines
        // either: the stepper can't remove those, so the field would claim
        // they had gone.
        const ceiling = Math.max(maxQty, current);
        const floor = CartSync.splitLines(variantId, store).locked;
        const newQty = Math.max(floor, Math.min(ceiling, requested));
        input.value = String(newQty);

        if (newQty === current) {
          if (requested < floor) {
            CartSync.showError('This item is in your cart as a promotion or gift. Change it from the cart.');
          }
          return;
        }

        // HC - B2B access, as on every other add-to-cart path: the controls
        // only render where the card shows prices (hideprice), and this checks
        // the product's tags with the client mirror before anything is added.
        // Taking items out is always allowed - 2026-09-26
        if (newQty > current) {
          const access = productAccess(qtyWrap);
          if (!access.allowed) {
            input.value = String(current);
            CartSync.showError(access.message);
            return;
          }
        }

        if (CartSync.cartTarget(variantId, newQty, store) === null) {
          input.value = String(current);
          CartSync.showError('This item is in your cart as a promotion or gift. Change it from the cart.');
          return;
        }

        if (newQty > current) {
          CartSync.showPendingToast();
        }

        await CartSync.update(variantId, newQty, store);
      };

      // HC - A write waits for the cart. Until it is known the field's 0 is
      // not the cart's quantity, and an absolute write built on it would cut
      // a line down (6 in the cart, "+" sends 1). The first read can fail, or
      // wait because the controls were hidden (ensureCart) - 2026-09-26
      // committed: the typed text Enter just saved; the change event as focus
      // leaves repeats it. A new edit, or a save that couldn't happen, clears
      // it, so the same number can be tried again.
      let committed = null;
      const whenCartKnown = (write) => {
        if (store.state.cartLoaded) {
          write();
          return;
        }
        CartSync.ensureCart(store).then((known) => {
          if (known) {
            write();
            return;
          }
          committed = null;
          input.value = String(effectiveCurrentQty());
          CartSync.showError(cartUnavailableMessage());
        });
      };

      // Step from the cart (or the queued target), not from the field's text,
      // which may be mid-edit or left stale while it had focus.
      minus?.addEventListener('click', () => {
        whenCartKnown(() => updateQty(effectiveCurrentQty() - 1));
      });

      plus?.addEventListener('click', () => {
        whenCartKnown(() => updateQty(effectiveCurrentQty() + 1));
      });

      // Only a whole number is written. Cleared means 0; anything else
      // ("abc", "12abc") puts the cart's quantity back and sends nothing.
      const commitTyped = () => {
        const raw = String(input.value || '').trim();
        if (raw === committed) return;
        if (raw !== '' && !/^\d+$/.test(raw)) {
          input.value = String(effectiveCurrentQty());
          return;
        }
        committed = raw;
        const requested = raw === '' ? 0 : parseInt(raw, 10);
        whenCartKnown(() => updateQty(requested));
      };
      input?.addEventListener('change', commitTyped);
      input?.addEventListener('input', () => {
        committed = null;
      });

      // HC - Enter saves the typed quantity and goes no further. In a form
      // (cart recommendations sit inside the cart form) it would otherwise
      // submit the form through its first button: Checkout - 2026-09-26
      input?.addEventListener('keydown', (evt) => {
        if (evt.key !== 'Enter') return;
        evt.preventDefault();
        commitTyped();
      });

      // A cart sync skips the focused field; catch it up when focus leaves
      // unless a typed change is on its way.
      input?.addEventListener('blur', () => {
        if (CartSync.pendingUpdates.has(variantId) || CartSync.activeVariants.has(variantId)) return;
        input.value = String(store.state.cartByVariant.get(variantId) || 0);
        applyAvailability(qtyWrap);
      });

      // Select all text on focus for easier editing
      input?.addEventListener('focus', () => {
        committed = null;
        input.select();
      });

      // Initialize from cart state
      const currentQty = store.state.cartByVariant.get(variantId) || 0;
      input.value = String(currentQty);
      applyAvailability(qtyWrap);

      // A stepper set up where the shopper can see it, before anything has
      // read the cart, reads it: one read however many steppers (ensureCart),
      // whoever sets them up (init, rebind, the color grid's own renders).
      // Hidden ones (a closed drawer, the menu) wait for a touch.
      if (!cartTracked() && isShown(qtyWrap)) CartSync.ensureCart(store);
    }
  };

  // An unavailable variant's stepper: + stays off; - and the field work while
  // some of it is still in the cart, so it can be removed; greyed out at 0.
  function applyAvailability(qtyWrap) {
    if (!qtyWrap || qtyWrap.dataset.available !== 'false') return;
    const minus = qtyWrap.querySelector('.cg-qty-btn--minus');
    const plus = qtyWrap.querySelector('.cg-qty-btn--plus');
    const input = qtyWrap.querySelector('.cg-qty-input');
    const empty = (parseInt(input && input.value, 10) || 0) <= 0;
    qtyWrap.classList.toggle('cg-qty--disabled', empty);
    qtyWrap.setAttribute('aria-disabled', empty ? 'true' : 'false');
    plus?.setAttribute('disabled', 'disabled');
    [minus, input].forEach((el) => {
      if (!el) return;
      if (empty) el.setAttribute('disabled', 'disabled');
      else el.removeAttribute('disabled');
    });
  }

  // HC - The product's B2B access (theme.evaluateProductAccessFromCarrier:
  // IBAccessPolicy on the product's tags). The tags ride on the nearest
  // [data-product-tags]: the card's controls (grid-product-atc), the modal's
  // variant list, or the product page root around its order table - 2026-09-26
  function productAccess(el) {
    const theme = window.theme;
    const carrier = el && typeof el.closest === 'function' ? el.closest('[data-product-tags]') : null;
    if (!carrier || !theme || typeof theme.evaluateProductAccessFromCarrier !== 'function') {
      return { allowed: true };
    }
    return theme.evaluateProductAccessFromCarrier(carrier);
  }

  function cartUnavailableMessage() {
    const cart = window.theme && window.theme.cart;
    return cart && typeof cart.isChallengeActive === 'function' && cart.isChallengeActive()
      ? 'Your session needs a quick security check. Please refresh the page and try again.'
      : 'We couldn\'t load your cart. Please try again.';
  }

  // Laid out and visible, so the shopper can see it: not in a closed drawer,
  // a hidden menu or anything else with display: none, nor under
  // visibility: hidden (the desktop menu's closed dropdown keeps its boxes).
  function isShown(el) {
    if (typeof el.getClientRects === 'function' && el.getClientRects().length === 0) return false;
    const style = typeof window.getComputedStyle === 'function' ? window.getComputedStyle(el) : null;
    return !style || style.visibility !== 'hidden';
  }

  function hasVisibleStepper(root) {
    return Array.prototype.some.call(root.querySelectorAll('.cg-qty'), (qty) => {
      return !(qty.dataset && qty.dataset.atcExternal) && isShown(qty);
    });
  }

  // Cart lines with properties (gift, promo, bundle) or a selling plan are
  // not the plain line a stepper adds to.
  function isPlainLine(item) {
    const props = item && item.properties;
    const hasProps = !!props && typeof props === 'object' &&
      Object.keys(props).some((name) => props[name] !== null && props[name] !== '');
    return !hasProps && !(item && item.selling_plan_allocation);
  }

  /* ========================================================================
     MODULE: OptionsPrefetch (Choose Options product, asked for on intent)
     ======================================================================== */
  // HC - The modal's product (/products/<handle>.js) is asked for as soon
  // as the shopper shows intent on a Choose Options button, so the options
  // are usually there when the modal opens: a mouse or pen resting on it for
  // HOVER_MS, a press (touch included) or keyboard focus (the listeners are
  // at the bottom of the file). One request per handle, shared with the
  // click and kept for TTL_MS: the JSON carries each variant's availability.
  // A failed prefetch is forgotten and never shown; the click asks again and
  // reports its own failure. Nothing is asked for during a cart security
  // check, with Save-Data on, or for a product the shopper can't buy (the
  // access check the click runs) - 2026-10-06
  const OptionsPrefetch = {
    HOVER_MS: 80,
    TTL_MS: 60000,
    entries: new Map(),
    hoverButton: null,
    hoverTimer: null,

    async request(handle) {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`);
      if (!res.ok) throw new Error('Product fetch failed');
      return res.json();
    },

    // An entry's at is when it answered (null while in flight), so a slow
    // request is shared, never asked for twice.
    stale(entry, now) {
      return entry.at !== null && now - entry.at > this.TTL_MS;
    },

    // The handle's request while it is in flight or fresh, else null.
    fresh(handle) {
      const entry = this.entries.get(handle);
      if (!entry) return null;
      if (!this.stale(entry, Date.now())) return entry;
      this.entries.delete(handle);
      return null;
    },

    start(handle, prefetch) {
      // Stale entries go as new ones come, so the cache stays small.
      const now = Date.now();
      this.entries.forEach((entry, key) => {
        if (this.stale(entry, now)) this.entries.delete(key);
      });
      const entry = { at: null, prefetch: !!prefetch, promise: this.request(handle) };
      // An answer is kept TTL_MS from when it came. A failure is forgotten,
      // so the next ask fetches again. This also keeps a prefetch nobody
      // clicked from rejecting unhandled.
      entry.promise.then(() => { entry.at = Date.now(); }, () => {
        if (this.entries.get(handle) === entry) this.entries.delete(handle);
      });
      this.entries.set(handle, entry);
      return entry.promise;
    },

    // For the modal: the prefetched product when there is one, else a new
    // request, as before. A prefetch that fails is asked once more here, so
    // the click still gets its own attempt (and its own error message).
    load(handle, retried) {
      const entry = this.fresh(handle);
      if (!entry) return this.start(handle, false);
      if (!entry.prefetch || retried) return entry.promise;
      return entry.promise.catch(() => this.load(handle, true));
    },

    // Intent on a Choose Options button: start its request, once.
    warm(btn) {
      const handle = btn && btn.dataset && btn.dataset.productHandle;
      if (!handle || btn.isConnected === false || this.fresh(handle)) return;
      const cart = window.theme && window.theme.cart;
      if (cart && typeof cart.isChallengeActive === 'function' && cart.isChallengeActive()) return;
      const connection = window.navigator && window.navigator.connection;
      if (connection && connection.saveData) return;
      if (!productAccess(btn).allowed) return;
      this.start(handle, true);
    },

    // A mouse or pen over a button: ask once it has rested there HOVER_MS.
    hoverStart(btn) {
      if (btn === this.hoverButton) return;
      this.hoverEnd();
      this.hoverButton = btn;
      this.hoverTimer = setTimeout(() => {
        this.hoverTimer = null;
        this.warm(btn);
      }, this.HOVER_MS);
    },

    hoverEnd() {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
      this.hoverButton = null;
    }
  };

  /* ========================================================================
     MODULE: VariantModal (Variant Picker Modal)
     ======================================================================== */
  const VariantModal = {
    modal: null,
    content: null,
    footerEl: null,
    titleEl: null,
    subtitleEl: null,
    closeBtn: null,
    thumbEl: null,
    linkEl: null,
    lastTrigger: null,
    variantPrices: new Map(),
    currentProduct: null,

    init(store) {
      this.modal = document.getElementById('cg-variant-modal');
      if (!this.modal) return;

      this.content = this.modal.querySelector('.cg-modal__content');
      // HC - Sticky footer container for mobile modal - 2026-01-22
      this.footerEl = this.modal.querySelector('[data-cg-modal-footer]');
      this.titleEl = this.modal.querySelector('#cg-modal-title');
      this.subtitleEl = this.modal.querySelector('[data-modal-subtitle]');
      this.closeBtn = this.modal.querySelector('.cg-modal__close');
      this.thumbEl = this.modal.querySelector('[data-modal-thumb]');
      this.linkEl = this.modal.querySelector('[data-modal-link]');
      this.store = store;

      this.bindEvents();
    },

    bindEvents() {
      const self = this;

      // Close button (header X)
      this.closeBtn?.addEventListener('click', () => self.close());

      // Header link - close modal when clicking
      this.linkEl?.addEventListener('click', () => self.close());

      // Backdrop click
      this.modal.addEventListener('click', (e) => {
        if (e.target === self.modal) self.close();
      });

      // HC - The modal can open from a card in the cart drawer or the menu,
      // which close on a click outside them and on Escape (listeners on the
      // window and the page). A click in the modal is not outside them, so it
      // stops here. The Escape that closes the modal stops too; its keyup
      // comes after focus went back to the card, so it is caught at the
      // window before it gets there - 2026-09-26
      this.modal.addEventListener('click', (e) => e.stopPropagation());

      // Escape key
      this.modal.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          self.swallowEscapeKeyup();
          self.close();
        } else {
          self.trapFocus(e);
        }
      });

      // Delegate click for options triggers
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('.cg-options-trigger');
        if (!btn) return;
        const productHandle = btn.dataset.productHandle;
        if (productHandle) {
          self.open(productHandle, btn);
        }
      });

      // Listen for cart updates to sync modal quantities
      document.addEventListener('cg:cart:updated', () => {
        if (self.modal.hasAttribute('open')) {
          self.syncQuantitiesFromStore();
        }
      });
    },

    swallowEscapeKeyup() {
      const swallow = (evt) => {
        if (evt.key !== 'Escape' && evt.keyCode !== 27) return;
        window.removeEventListener('keyup', swallow, true);
        evt.stopPropagation();
      };
      window.addEventListener('keyup', swallow, true);
      setTimeout(() => window.removeEventListener('keyup', swallow, true), 1000);
    },

    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const focusableSelector = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
      const focusable = Array.from(this.modal.querySelectorAll(focusableSelector));
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },

    focusFirstElement() {
      const focusableSelector = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
      const focusable = this.modal.querySelectorAll(focusableSelector);
      if (focusable.length) {
        focusable[0].focus();
      } else if (this.closeBtn) {
        this.closeBtn.focus();
      }
    },

    formatMoney(amount) {
      try {
        if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
          return Shopify.formatMoney(amount);
        }
      } catch (e) {}
      if (typeof amount !== 'number') return '—';
      return '$' + (amount / 100).toFixed(2);
    },

    normalizeMoney(value) {
      if (value == null || value === '') return null;
      if (typeof value === 'number') return value;
      if (typeof value === 'string') {
        if (value.includes('.')) {
          const parsed = parseFloat(value);
          return Number.isNaN(parsed) ? null : Math.round(parsed * 100);
        }
        const parsedInt = parseInt(value, 10);
        return Number.isNaN(parsedInt) ? null : parsedInt;
      }
      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    },

    escapeHtml(str) {
      return String(str || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },

    getVariantImage(variant, product) {
      const variantImage = variant?.featured_image;
      if (variantImage) {
        return variantImage.src || variantImage.url || (typeof variantImage === 'string' ? variantImage : null);
      }
      if (product?.featured_image) return product.featured_image;
      if (Array.isArray(product?.images) && product.images.length) {
        const firstImage = product.images[0];
        return firstImage?.src || firstImage?.url || firstImage;
      }
      return null;
    },

    getHeroImage(product) {
      if (product?.featured_image) return product.featured_image;
      if (Array.isArray(product?.images) && product.images.length) {
        const hero = product.images[0];
        return hero?.src || hero?.url || hero;
      }
      return null;
    },

    isVariantAvailable(variant) {
      if (!variant) return false;
      if (typeof variant.available === 'boolean') return variant.available;
      if (typeof variant.available === 'string') return variant.available !== 'false';
      if (typeof variant.inventory_quantity === 'number') return variant.inventory_quantity > 0;
      return true;
    },

    formatLeadPrice(product, variants) {
      const cents = variants
        .map((variant) => this.normalizeMoney(variant.price))
        .filter((value) => typeof value === 'number')
        .sort((a, b) => a - b)[0];
      const priceText = typeof cents === 'number' ? this.formatMoney(cents) : '—';
      return `From ${priceText}`;
    },

    syncQuantitiesFromStore() {
      this.variantPrices.forEach((_, variantId) => {
        // Don't overwrite input if there's a pending update for this variant
        const hasPending = CartSync.activeVariants.has(variantId) ||
                           CartSync.pendingUpdates.has(variantId);
        if (hasPending) return;

        const qty = this.store.state.cartByVariant.get(variantId) || 0;
        const input = this.content.querySelector(`.cg-qty[data-variant-id="${variantId}"] .cg-qty-input`);
        if (input) input.value = qty;
      });
    },

    renderVariants(product) {
      const self = this;
      this.currentProduct = product;
      if (this.footerEl) this.footerEl.innerHTML = '';
      const variants = Array.isArray(product?.variants) ? product.variants : [];

      if (this.titleEl) this.titleEl.textContent = product?.title || 'Choose Options';

      if (!variants.length) {
        if (this.subtitleEl) this.subtitleEl.textContent = 'Currently unavailable';
        this.content.innerHTML = '<p>No variants available for this product.</p>';
        return;
      }

      if (this.subtitleEl) this.subtitleEl.textContent = this.formatLeadPrice(product, variants);
      this.variantPrices = new Map();

      const variantMarkup = variants.map((variant) => {
        const price = this.normalizeMoney(variant.price);
        const compareAt = this.normalizeMoney(variant.compare_at_price);
        const showCompare = compareAt != null && price != null && compareAt > price;
        const available = this.isVariantAvailable(variant);
        const imageSrc = this.getVariantImage(variant, product);
        const variantTitle = variant.title === 'Default Title' ? product.title : variant.title;
        this.variantPrices.set(variant.id, typeof price === 'number' ? price : 0);

        const priceMarkup = price != null
          ? `<span class="price${showCompare ? ' cg-price--sale' : ''}">${this.formatMoney(price)}</span>`
          : '<span class="price">—</span>';
        const compareMarkup = showCompare ? `<span class="cg-price--compare">${this.formatMoney(compareAt)}</span>` : '';
        const saleBadge = showCompare ? '<span class="cg-modal__variant-sale-badge">Sale</span>' : '';

        const quantityMarkup = available ? `
          <div class="cg-actions">
            <div class="cg-qty" data-variant-id="${variant.id}" data-sku="${this.escapeHtml(variant.sku || '')}" data-available="true">
              <button class="cg-qty-btn cg-qty-btn--minus" type="button" aria-label="Decrease quantity">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                  <path d="M5 12h14"/>
                </svg>
              </button>
              <input
                class="cg-qty-input"
                type="number"
                value="0"
                min="0"
                max="99"
                aria-label="Quantity for ${this.escapeHtml(variantTitle)}">
              <button class="cg-qty-btn cg-qty-btn--plus" type="button" aria-label="Increase quantity">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                  <path d="M12 5v14M5 12h14"/>
                </svg>
              </button>
            </div>
            <div class="cg-pending-status" aria-hidden="true">
              <span>Updating...</span>
            </div>
          </div>
        ` : `<span class="cg-sold-out" aria-live="polite">Sold out</span>`;

        return `
          <article class="cg-modal__variant" data-variant-id="${variant.id}">
            <div class="cg-modal__cell cg-modal__cell--variant">
              <div class="cg-modal__variant-image">
                ${imageSrc ? `<img src="${this.escapeHtml(imageSrc)}" alt="${this.escapeHtml(variantTitle)}">` : `<div class="cg-variant-placeholder">${this.escapeHtml((variantTitle || '?').charAt(0))}</div>`}
              </div>
              <div class="cg-modal__variant-info">
                <div class="cg-modal__variant-titlerow">
                  <h3 class="cg-modal__variant-title">${this.escapeHtml(variantTitle)}</h3>
                  ${saleBadge}
                </div>
                ${variant.sku ? `<span class="cg-modal__variant-sku">${this.escapeHtml(variant.sku)}</span>` : ''}
              </div>
            </div>
            <div class="cg-modal__cell cg-modal__cell--quantity">
              ${quantityMarkup}
            </div>
            <div class="cg-modal__cell cg-modal__cell--price">
              <div class="cg-modal__variant-price">
                ${priceMarkup}
                ${compareMarkup}
              </div>
            </div>
          </article>
        `;
      }).join('');

      const heroImage = this.getHeroImage(product);
      const heroMarkup = heroImage
        ? `<img src="${this.escapeHtml(heroImage)}" alt="${this.escapeHtml(product?.title || '')}">`
        : `<div class="cg-variant-placeholder">${this.escapeHtml((product?.title || '?').charAt(0))}</div>`;

      const productUrl = product?.handle ? `${window.Shopify?.routes?.root || '/'}products/${product.handle}` : '#';
      const footerInnerMarkup = `
        <div class="cg-modal__footer-buttons">
          <button class="cg-modal__btn cg-modal__btn--cart" type="button" data-view-cart-mobile>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
            </svg>
            Cart
          </button>
          <button class="cg-modal__btn cg-modal__btn--close" type="button" data-close-modal>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            Done
          </button>
        </div>
      `;
      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      const panelFooterMarkup = isMobile ? '' : `<footer class="cg-modal__footer">${footerInnerMarkup}</footer>`;

      this.content.innerHTML = `
        <div class="cg-modal__layout">
          <!-- Compact mobile header (hidden on desktop) -->
          <div class="cg-modal__product-compact">
            <div class="cg-modal__product-thumb">
              ${heroMarkup}
            </div>
            <div class="cg-modal__product-info">
              <strong class="cg-modal__product-title">${this.escapeHtml(product?.title || '')}</strong>
              <span class="cg-modal__product-price">${this.formatLeadPrice(product, variants)}</span>
              <a class="cg-modal__link" href="${productUrl}" data-view-product>
                View full details →
              </a>
            </div>
          </div>
          <!-- Desktop aside (hidden on mobile) -->
          <aside class="cg-modal__product">
            <div class="cg-modal__media">
              ${heroMarkup}
            </div>
            <div class="cg-modal__details">
              <strong>${this.escapeHtml(product?.title || '')}</strong>
              <span>${this.formatLeadPrice(product, variants)}</span>
            </div>
            <a class="cg-modal__link" href="${productUrl}" data-view-product>
              View full details →
            </a>
          </aside>
          <div class="cg-modal__panel">
            <div>
              <div class="cg-modal__table-header">
                <span>Variant</span>
                <span>Quantity</span>
                <span>Price</span>
              </div>
              <div class="cg-modal__variant-list" data-product-tags="${this.escapeHtml(encodeURIComponent(JSON.stringify(product?.tags || [])))}" data-product-skus="${this.escapeHtml(encodeURIComponent(JSON.stringify((product?.variants || []).map((v) => v && v.sku))))}">
                ${variantMarkup}
              </div>
            </div>
            ${panelFooterMarkup}
          </div>
        </div>
      `;
      if (this.footerEl) this.footerEl.innerHTML = isMobile ? footerInnerMarkup : '';

      // View product links (both mobile and desktop)
      this.content.querySelectorAll('[data-view-product]').forEach(link => {
        link.addEventListener('click', () => self.close());
      });

      // View cart button - open cart drawer instead of navigating
      this.modal.querySelectorAll('[data-view-cart-mobile]').forEach((btn) => {
        btn.addEventListener('click', () => {
          self.close();
          // Dispatch cart:open event to open the cart drawer
          document.dispatchEvent(new CustomEvent('cart:open'));
        });
      });

      // Close buttons (mobile footer)
      this.modal.querySelectorAll('[data-close-modal]').forEach((btn) => {
        btn.addEventListener('click', () => self.close());
      });

      this.content.querySelectorAll('.cg-modal__variant').forEach((variantEl) => {
        QuantityManager.setup(variantEl, this.store);
      });

      this.syncQuantitiesFromStore();
      this.focusFirstElement();
    },

    async open(productHandle, triggerEl) {
      if (!productHandle || !this.modal) return;
      // HC - B2B access before the options open (the tags ride on the card's
      // controls); the modal's steppers check the fetched product's tags too.
      // Its steppers show the cart's quantities: read it if nothing has yet
      // - 2026-09-26
      const access = productAccess(triggerEl);
      if (!access.allowed) {
        CartSync.showError(access.message);
        return;
      }
      CartSync.ensureCart(this.store);
      this.lastTrigger = triggerEl || null;

      try {
        if (typeof this.modal.showModal === 'function') {
          this.modal.showModal();
        } else {
          this.modal.setAttribute('open', 'true');
        }
      } catch (e) {
        this.modal.setAttribute('open', 'true');
      }

      if (this.subtitleEl) this.subtitleEl.textContent = 'Loading product details…';
      this.content.innerHTML = '<div class="cg-loading"><div class="cg-spinner" aria-hidden="true"></div><span>Loading options...</span></div>';
      if (this.footerEl) this.footerEl.innerHTML = '';
      this.focusFirstElement();

      try {
        // HC - Usually asked for already, on intent (OptionsPrefetch); if it
        // has arrived the options replace the spinner before it paints
        // - 2026-10-06
        const product = await OptionsPrefetch.load(productHandle);
        this.renderVariants(product);
      } catch (e) {
        console.error('VariantModal: failed to load product', e);
        this.content.innerHTML = '<p>Failed to load options. Please try again.</p>';
        if (this.footerEl) this.footerEl.innerHTML = '';
        CartSync.announce('Failed to load product options');
      }
    },

    close() {
      if (typeof this.modal.close === 'function') {
        try { this.modal.close(); } catch (e) { this.modal.removeAttribute('open'); }
      } else {
        this.modal.removeAttribute('open');
      }
      // Back to the card's button, unless it has gone from view since (its
      // drawer or menu closed).
      const trigger = this.lastTrigger;
      this.lastTrigger = null;
      if (trigger && trigger.isConnected !== false && typeof trigger.focus === 'function' && isShown(trigger)) {
        trigger.focus();
      }
    }
  };

  /* ========================================================================
     INITIALIZATION
     ======================================================================== */
  function syncAllQuantities() {
    syncQuantities(document);
  }

  // HC - showPending (a freshly rendered card) paints a +/- still on its way
  // at its target: the card has no typed value to protect, and its next tap
  // must build on that target, not the confirmed quantity. - 2026-09-25
  function syncQuantities(root, showPending) {
    root.querySelectorAll('.cg-qty').forEach((qtyContainer) => {
      // Contract: steppers marked data-atc-external have their cart writes owned
      // by another module. CollectionATC must not bind quantity controls or sync
      // quantities into them.
      if (qtyContainer.dataset.atcExternal) return;
      const variantId = Number(qtyContainer.dataset.variantId);
      if (!variantId) return;

      // Don't overwrite input if there's a pending update for this variant
      // This prevents the "flicker" when user clicks rapidly
      const hasPending = CartSync.activeVariants.has(variantId) ||
                         CartSync.pendingUpdates.has(variantId);
      if (hasPending) {
        if (showPending && CartSync.pendingUpdates.has(variantId)) {
          const pendingInput = qtyContainer.querySelector('.cg-qty-input');
          if (pendingInput) pendingInput.value = CartSync.pendingUpdates.get(variantId);
        }
        return;
      }

      const qty = Store.state.cartByVariant.get(variantId) || 0;
      const input = qtyContainer.querySelector('.cg-qty-input');
      // HC - Never overwrite the field the shopper is typing in: another
      // stepper's update can land mid-typing; change syncs it after - 2026-09-25
      if (input && input === document.activeElement) return;
      if (input) input.value = String(qty);
      applyAvailability(qtyContainer);
    });
  }

  // Whether the page has read the cart, or is reading it: then it keeps up.
  function cartTracked() {
    return !!(Store.state.cartLoaded || CartSync.cartRead);
  }

  async function init() {
    // HC - Nothing to do on a page without these controls (they can appear
    // later: rebind and bindOnTouch call init then) - 2026-09-26
    if (!document.querySelector('.cg-actions, .cg-actions-wrap, .cg-options-trigger, #color-grid')) {
      return;
    }
    if (window[COLLECTION_ATC_BINDINGS_READY]) {
      return;
    }
    if (collectionATCInitPromise) {
      return collectionATCInitPromise;
    }

    collectionATCInitPromise = (async () => {
      // HC - Bind first, then read the cart: the controls answer straight
      // away (a write waits for the cart, whenCartKnown), and a grid only in
      // a closed drawer or hidden menu doesn't read it at all until it is
      // used - 2026-09-26
      // Set up quantity controls on all cards
      document.querySelectorAll('.cg-actions').forEach((container) => {
        QuantityManager.setup(container, Store);
      });

      // Initialize modal
      VariantModal.init(Store);

      // Subscribe to state changes to sync quantities
      Store.subscribe(() => {
        syncAllQuantities();
      });

      // Listen for external cart changes
      let cartBuildTimer = null;
      document.addEventListener('cart:build', (evt) => {
        if (evt && evt.detail && evt.detail.source === 'collection-atc') return;
        clearTimeout(cartBuildTimer);
        cartBuildTimer = setTimeout(async () => {
          if (cartTracked()) await CartSync.fetchCart(Store);
        }, 200);
      });

      document.addEventListener('ajaxProduct:added', async (evt) => {
        const detail = evt && evt.detail ? evt.detail : {};
        const cartState = window.theme.cart.getCartState(detail);

        if (cartState) {
          CartSync.syncCart(Store, cartState);
          return;
        }

        // Skip only our own no-payload events; skipCartOpen is drawer policy, not ownership.
        if (detail.source === 'collection-atc') return;

        // Delay slightly to ensure cart has updated
        setTimeout(async () => {
          if (cartTracked()) await CartSync.fetchCart(Store);
        }, 100);
      });
      window[COLLECTION_ATC_BINDINGS_READY] = true;

      // Fetch initial cart state, when a stepper is showing
      if (hasVisibleStepper(document)) {
        await CartSync.ensureCart(Store);
      }
    })();

    try {
      await collectionATCInitPromise;
    } catch (err) {
      collectionATCInitPromise = null;
      throw err;
    }
  }

  // Public facade for modules that inject CollectionATC-compatible quick-add
  // markup after page load. First call delegates to init() so document-level
  // listeners/subscriptions stay one-time; later calls bind only the injected
  // scope and refresh cart state. NEVER call a CartSync.syncAll-style bulk sync
  // here: external steppers marked data-atc-external must keep local quantities.
  // HC - options.fromState paints the scope's steppers from the cart state this
  // module already tracks instead of reading /cart.js again. Collection filters
  // v2 re-renders the grid on every filter change and passes it. - 2026-09-25
  function rebind(scope, options) {
    if (!window[COLLECTION_ATC_BINDINGS_READY]) {
      return init();
    }

    const root = scope || document;
    if (root && root.querySelectorAll) {
      root.querySelectorAll('.cg-actions').forEach((container) => {
        QuantityManager.setup(container, Store);
      });
    }

    if (options && options.fromState) {
      if (root && root.querySelectorAll) {
        syncQuantities(root, true);
        // The page hasn't needed the cart yet, and now shows a stepper.
        if (!cartTracked() && hasVisibleStepper(root)) return CartSync.ensureCart(Store);
      }
      return Promise.resolve();
    }
    return CartSync.fetchCart(Store);
  }

  // When the edge challenge clears, first REHYDRATE cart state, then drain any
  // parked quick-add updates. While challenged, fetchCart early-outs (and the
  // initial read returns null after arming the flag), so Store.cartByVariant can
  // still be the empty initial Map and every qty input shows 0. Re-reading the
  // true cart now resyncs those inputs via the Store subscription, so a later
  // +/- can't send a stale 0 as an ABSOLUTE quantity and clobber an existing
  // line (the handler seeds its target from cartByVariant). The rehydrate runs
  // even with nothing queued — that no-queue case is exactly the silent
  // overwrite. Then drain: processQueue early-outs during a challenge and does
  // NOT self-reschedule, so without this wake-up parked work would strand until
  // the next manual +/- click. Registered at load; it does nothing on a page
  // whose controls were never set up and has nothing parked, so a page
  // without them doesn't read the cart. - 2026-09-26
  if (typeof document !== 'undefined' && document.addEventListener) {
    document.addEventListener('cart:challenge:cleared', function() {
      if (!window[COLLECTION_ATC_BINDINGS_READY] && CartSync.pendingUpdates.size === 0) return;
      CartSync.fetchCart(Store).finally(function() {
        if (CartSync.pendingUpdates.size > 0) {
          CartSync.processQueue(Store).finally(function() {
            if (CartSync.pendingUpdates.size === 0) {
              CartSync.clearPendingToast();
            }
          });
        } else {
          CartSync.clearPendingToast();
        }
      });
    });

    // HC - Other modules' cart writes (cart drawer line changes and removals,
    // the cart page) broadcast only cart:updated, with the verified cart.
    // Syncing Store from it keeps every stepper current, and lets
    // rebind(scope, { fromState: true }) trust Store. Our own writes are
    // skipped (processQueue syncs those itself), as are optimistic events,
    // whose lines are a guess. A count-only event syncs when it carries the
    // verified cart (the stock drawer's change when it can't re-render), and
    // is a no-op when it carries only a count. - 2026-09-25
    // HC - A page restored from the back/forward cache kept Store from before
    // it was left; the cart may have changed on the page in between (a PDP
    // add). One read brings every stepper up to date, and a step waits for
    // it. - 2026-09-25
    // Only where the steppers were set up and the cart had been read:
    // elsewhere nothing reads Store yet.
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      window.addEventListener('pageshow', function(evt) {
        if (!evt || !evt.persisted || !window[COLLECTION_ATC_BINDINGS_READY] || !cartTracked()) return;
        Store.setState({ cartLoaded: false });
        CartSync.ensureCart(Store);
      });
    }

    document.addEventListener('cart:updated', function(evt) {
      const detail = evt && evt.detail ? evt.detail : {};
      if (detail.source === 'collection-atc' || detail.optimistic) return;
      const cart = window.theme && window.theme.cart;
      const cartState = cart && typeof cart.getCartState === 'function' ? cart.getCartState(detail) : null;
      if (cartState) CartSync.syncCart(Store, cartState);
    });

    // HC - A control sets itself up the first time it is touched, so a grid
    // put on the page without theme.QuickAdd or rebind (a theme editor
    // section reload, a new loader) still works. Capture phase, on
    // pointerdown and focus: before the tap's click reaches the button
    // - 2026-09-26
    const bindOnTouch = function(evt) {
      const target = evt && evt.target;
      if (!target || typeof target.closest !== 'function') return;
      const actions = target.closest('.cg-actions');
      if (!actions && !target.closest('.cg-options-trigger')) return;
      if (!window[COLLECTION_ATC_BINDINGS_READY]) {
        init().catch((err) => console.error('CollectionATC: init failed', err));
      }
      const qty = actions && actions.querySelector('.cg-qty');
      if (qty && !qty.dataset.spQtyBound) QuantityManager.setup(actions, Store);
    };
    document.addEventListener('pointerdown', bindOnTouch, true);
    document.addEventListener('focusin', bindOnTouch, true);

    // HC - Choose Options asks for its product on intent (OptionsPrefetch):
    // a press or focus at once; a mouse or pen once it has rested on the
    // button, so a pointer sweeping across a grid asks for nothing. Touch
    // hover is left to the press that follows it. Delegated, in the capture
    // phase like bindOnTouch, so grids rendered later (filters, the menu,
    // the cart drawer) are covered - 2026-10-06
    const optionsTrigger = function(evt) {
      const target = evt && evt.target;
      return target && typeof target.closest === 'function' ? target.closest('.cg-options-trigger') : null;
    };
    const warmOnPress = function(evt) {
      const btn = optionsTrigger(evt);
      if (btn) OptionsPrefetch.warm(btn);
    };
    document.addEventListener('pointerdown', warmOnPress, true);
    document.addEventListener('focusin', warmOnPress, true);
    document.addEventListener('pointerover', function(evt) {
      if (evt.pointerType === 'touch') return;
      const btn = optionsTrigger(evt);
      if (btn) OptionsPrefetch.hoverStart(btn);
    }, true);
    document.addEventListener('pointerout', function(evt) {
      const btn = optionsTrigger(evt);
      if (!btn || btn !== OptionsPrefetch.hoverButton) return;
      // Onto the button's own text or icon is still on the button.
      const to = evt.relatedTarget;
      if (to && typeof btn.contains === 'function' && btn.contains(to)) return;
      OptionsPrefetch.hoverEnd();
    }, true);
  }

  // Auto-initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return {
    init,
    rebind,
    Store,
    CartSync,
    QuantityManager,
    VariantModal
  };
})();

// Export for external use
window.CollectionATC = CollectionATC;
